import moment from 'moment';

import { DatePickerMode } from '@philips-emr/tws-components-api';

import { getMonthGrid } from '../calendar/calendar-util';

const CONSTANTS = {
  DAY: 'day',
  HOUR: 'hour',
  MONTH: 'month',
  WEEK: 'week',
  MINUTE: 'minute',
  YEAR: 'year',
};

export function getYearGrid(date, events) {
  const copy = moment.utc(date);
  const years = [[]];

  const year = {
    date: copy,
    events,
    active: true,
  };

  years[0].push(getCalendarDateItem(year));

  for (let index = 0; index <= 10; index++) {
    copy.add(-1, 'year');

    const newYear = {
      date: copy,
      events,
      active: true,
    };

    years[0].unshift(getCalendarDateItem(newYear));
  }

  const montGrid = {
    numOfRows: 3,
    sizeOfRows: 4,
    array: years,
  };

  return sliceArray(montGrid);
}

export function getAllMonthsGrid(date, events) {

  const lastMonth = moment.utc(date).endOf(CONSTANTS.YEAR).add(1, CONSTANTS.DAY);

  return getAllMonths(lastMonth, events, []);
}

export function getGranularity(mode) {
  switch (mode) {
    case DatePickerMode.MONTHLY :
      return 'date';
    case DatePickerMode.YEARLY :
      return 'month';
    case DatePickerMode.DECADES :
      return 'year';
  }
}

export function incrementMode(mode) {
  switch (mode) {
    case DatePickerMode.MONTHLY:
      return DatePickerMode.YEARLY;
    default:
      return DatePickerMode.DECADES;
  }
}

export function decrementMode(mode) {
  switch (mode) {
    case DatePickerMode.DECADES:
      return DatePickerMode.YEARLY;
    default:
      return DatePickerMode.MONTHLY;
  }
}

function getAllMonths(date, events, months) {
  const copy = moment.utc(date);

  months[0] = [];

  for (let index = 0; index <= 11; index++) {
    copy.add(-1, CONSTANTS.MONTH);
    const month = {
      date: copy,
      events,
      active: true,
    };
    months[0].unshift(getCalendarDateItem(month));
  }

  const montGrid = {
    numOfRows: 3,
    sizeOfRows: 4,
    array: months,
  };

  return sliceArray(montGrid);
}

/**
 * This method transform a array in a grid,
 * the product of numOfRows X sizeOfRows need be a pair number
 * @param {numOfRows, sizeOfRows, array}
 * @returns {Array}
 */
function sliceArray({ numOfRows, sizeOfRows, array }) {
  const part = [];

  if (array[0].length % numOfRows === 0) {
    let numOfPartsCount = 0;
    while (part.length !== numOfRows) {
      part.push(array[0].slice(numOfPartsCount, numOfPartsCount+sizeOfRows));
      numOfPartsCount += sizeOfRows;
    }
  }

  return part;
}

function getCalendarDateItem({ date, events = [], active }) {
  const copy = moment.utc(date);

  return {
    active: active,
    date: copy,
    events: getDayEvents(date, events),
  };
}

function getDayEvents(date, events = []) {
  return events
    .map((event) => ({ ...event, date: moment.utc(event.date) }))
    .filter((event) => event.date.isSame(date, 'date'));
}

export function getMonthGridInterval(date, events, startIntervalDate, endIntervalDate) {
  const startOfMonth = moment.utc(date).startOf(CONSTANTS.MONTH);

  const nextMonth = moment
    .utc(startOfMonth)
    .add(1, CONSTANTS.MONTH)
    .startOf(CONSTANTS.DAY)
    .startOf(CONSTANTS.MONTH);

  return getFirstDaysOfMonth(
    nextMonth,
    events,
    getDaysOfMonth(
      startOfMonth,
      date,
      events,
      getLastDaysOfMonth(startOfMonth, events, [], startIntervalDate, endIntervalDate),
      startIntervalDate,
      endIntervalDate
    )
  );
}

function getFirstDaysOfMonth(date, events, weeks, startIntervalDate, endIntervalDate) {
  const lastWeek = weeks[weeks.length - 1];
  const copy = moment.utc(date);

  for (let index = lastWeek.length; index < 7; index++) {
    lastWeek.push(getDay(copy, events, false, startIntervalDate, endIntervalDate));

    copy.add(1, CONSTANTS.DAY);
  }

  return weeks;
}

function getDay(date, events = [], active = true, startIntervalDate, endIntervalDate) {
  const copy = moment.utc(date);

  let dateInInterval = false;
  if (startIntervalDate !== undefined && endIntervalDate !== undefined){
    dateInInterval = moment.utc(date).isBetween(moment.utc(startIntervalDate), moment.utc(endIntervalDate));
  }

  return {
    active,
    date: copy,
    events: getDayEvents(date, events),
    isDateInInterval: dateInInterval,
  };
}

function getDaysOfMonth(date, month, events, weeks, startIntervalDate, endIntervalDate) {
  let weekIndex = 0;
  const copy = moment.utc(date);

  while (copy.isSame(month, CONSTANTS.MONTH)) {
    if (weeks[weekIndex].length >= 7) {
      weeks.push([]);
      weekIndex += 1;
    }

    weeks[weekIndex].push(getDay(copy, events, true, startIntervalDate, endIntervalDate));

    copy.add(1, CONSTANTS.DAY);
  }

  return weeks;
}

function getLastDaysOfMonth(date, events, weeks, startIntervalDate, endIntervalDate) {
  const difference = date.day() - moment.localeData().firstDayOfWeek();
  const copy = moment.utc(date);

  weeks[0] = [];

  for (let index = 0; index < difference; index++) {
    copy.add(-1, CONSTANTS.DAY);

    weeks[0].unshift(getDay(copy, events, false, startIntervalDate, endIntervalDate));
  }

  return weeks;
}

export function getCalendarTitleFormat(mode, date) {
  switch (mode) {
    case DatePickerMode.MONTHLY:
      return date.format('DD');
    case DatePickerMode.YEARLY:
      return date.format('MMM');
    case DatePickerMode.DECADES:
      return date.format('YYYY');
  }
}

export function getTypeOfNavigabilityMode(mode) {
  if (mode === DatePickerMode.MONTHLY) {
    return 'month';
  } else {
    return 'year';
  }
}

export function getDataGrid(options) {
  const { events, date, endIntervalDate, isIntervalMode = false, mode, startIntervalDate } = options;

  if (isIntervalMode) {
    return getMonthGridInterval(date, events, startIntervalDate, endIntervalDate);
  } else {
    switch (mode) {
      case DatePickerMode.MONTHLY:
        return getMonthGrid(date, events);
      case DatePickerMode.YEARLY:
        return getAllMonthsGrid(date, events);
      case DatePickerMode.DECADES:
        return getYearGrid(date, events);
    }
  }
}

export function isSelectedAndActive(isSelected, active, mode) {
  return isSelected && active || isSelected && mode !== DatePickerMode.MONTHLY;
}

export function isVisible(isIntervalMode, active) {
  return isIntervalMode && !active;
}

export function isCurrent(isIntervalMode, date) {
  return !isIntervalMode && moment().utc().isSame(date, 'date');
}

export function hasEvents(isIntervalMode, events) {
  return !isIntervalMode && events && events.length;
}

export function isSelected(isIntervalMode, isDateInInterval, startIntervalDate, date, endIntervalDate, currentValue, mode) {
  return isIntervalMode
    ? (isDateInInterval
      || startIntervalDate && moment(startIntervalDate).utc().isSame(date.utc(), 'date')
      || endIntervalDate && moment(endIntervalDate).utc().isSame(date.utc(), 'date'))
    : moment(date).isSame(currentValue, getGranularity(mode));
}

export function isLimit(isFirstLimit, isLastLimit) {
  return isFirstLimit === true || isLastLimit === true;
}

export function isIntervalLimit(intervalDate, date) {
  return intervalDate && intervalDate.isSame(date, 'date');
}
